import { billingService } from "@/services";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { qk } from "./queryKeys";

export function useInvoices() {
	return useQuery({
		queryKey: qk.invoices,
		queryFn: ({ signal }) => billingService.listInvoices(signal),
	});
}

/**
 * Live quote for a seat purchase.
 *
 * Enabled only for a sensible number, so typing in the field doesn't fire a
 * request per keystroke. Quoting has no side effects, but it is still a round
 * trip and the figure should feel settled rather than flickering.
 */
export function useSeatQuote(additional: number, enabled = true) {
	return useQuery({
		queryKey: qk.seatQuote(additional),
		queryFn: ({ signal }) => billingService.quoteSeats(additional, signal),
		enabled: enabled && Number.isInteger(additional) && additional > 0,
		staleTime: 60_000,
	});
}

/** Live price preview for first-run setup. */
export function useSetupQuote(seats: number, code: string, enabled = true) {
	return useQuery({
		queryKey: qk.setupQuote(seats, code),
		queryFn: ({ signal }) =>
			billingService.setupQuote(seats, code || undefined, signal),
		// Only for a sensible number, so typing in the field does not fire a
		// request per keystroke.
		enabled: enabled && Number.isInteger(seats) && seats > 0,
		staleTime: 60_000,
	});
}

export function useCompleteSetup() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			seats,
			code,
			billingEmail,
		}: { seats: number; code?: string; billingEmail?: string }) =>
			billingService.completeSetup(seats, code, billingEmail),
		// The org is a different thing afterwards: seats, contract, discount.
		onSuccess: () => qc.invalidateQueries({ queryKey: qk.org }),
	});
}

/** Schedule the seat count for the next renewal. */
export function useScheduledSeats() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (seats: number | null) =>
			billingService.setScheduledSeats(seats),
		onSuccess: () => qc.invalidateQueries({ queryKey: qk.org }),
	});
}

/** Re-check one invoice against the payment provider. */
export function useRefreshInvoice() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => billingService.refreshInvoice(id),
		onSuccess: () => qc.invalidateQueries({ queryKey: qk.invoices }),
	});
}

export function useBuySeats() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			additional,
			expectedCurrentSeats,
		}: { additional: number; expectedCurrentSeats: number | null }) =>
			billingService.buySeats(additional, expectedCurrentSeats),
		// Seats are granted at issue, so the roster and header are stale the
		// moment this succeeds.
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: qk.org });
			qc.invalidateQueries({ queryKey: qk.members() });
			qc.invalidateQueries({ queryKey: qk.invoices });
		},
	});
}
